import { useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { socket } from "src/utils/socket";
import axios from "src/utils/axios";
import { store, useDispatch } from "src/redux/store";
import { useSnackbar } from "src/components/snackbar";
import { useAuthContext } from "src/auth/useAuthContext";
import { getOrderAndDraftCount } from "src/redux/slices/orderAndDraftCountSlice";
import { autoPrint } from "src/utils/printToIP";
import { filterOutVoidItems } from "src/utils/filterVoidItems";
import {
  getDefaultReceipt,
  getDefaultPrinter,
  getPrintersForReceipts,
  getFailedPrinterNames,
  hasStationDataChanged,
} from "src/utils/posLayout/classicalPOSReceiptUtils";
import {
  KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT,
  KITCHEN_RECEIPT_MASTER_ACCOUNT_OPEN_ORDERS_REFRESH,
  KITCHEN_RECEIPT_PRINT_STATUS_EVENT,
  KITCHEN_RECEIPT_PRINT_STATUS,
  isFinalKitchenReceiptPrintStatus,
  runKitchenReceiptMasterAccountExclusive,
  getOrderVersionKey,
  isOrderVersionProcessed,
  markOrderVersionProcessed,
  saveOrderSnapshot,
  getItemsForStationChangeCheck,
} from "src/utils/posLayout/kitchenReceiptMasterAccountUtils";
import { KitchenReceiptThroughIPAutoStation } from "src/sections/@dashboard/pos/invoice/KitchenReceiptThroughIPAutoStation";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const getEntityId = (value) => String(value?.id || value?._id || value || "");

// Temporary trace for the master account print flow; filter the console by "[kitchen-print]".
const debugKitchenPrint = (...args) => console.info("[kitchen-print]", ...args);

// How long the ordering user waits for a final print status before warning that none arrived.
const PRINT_STATUS_TIMEOUT_MS = 60 * 1000;

const getSelectedLevelId = (user) => {
  try {
    return JSON.parse(sessionStorage.getItem("levelSelected"))?.id || user?.levelId;
  } catch (error) {
    return user?.levelId;
  }
};

export default function GlobalKitchenReceiptMasterAccountListener() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const userRef = useRef(user);
  userRef.current = user;
  const printStatusTimersRef = useRef(new Map());
  const shownPrintStatusesRef = useRef(new Set());

  useEffect(() => {
    if (!user?.id) return undefined;

    // Existing master account print feedback: the print service's success message and failed station names.
    const showPrintResults = (results, printers) => {
      const successResult = results.find((res) => res.success);
      const failedIPs = results.filter((res) => !res.success).map((res) => `${res.ipAddress}`);

      if (successResult) {
        enqueueSnackbar(successResult.message, { variant: "success" });
      }
      if (failedIPs.length > 0) {
        enqueueSnackbar(`Print Failed\n${getFailedPrinterNames(printers, failedIPs).join(",\n")}`, {
          variant: "error",
          autoHideDuration: 6000,
          style: { whiteSpace: "pre-line" },
        });
      }
    };

    const clearPrintStatusTimer = (jobKey) => {
      clearTimeout(printStatusTimersRef.current.get(jobKey));
      printStatusTimersRef.current.delete(jobKey);
    };

    // Shows a print lifecycle notification once per job and status, whether it came from this tab or the socket.
    const notifyPrintStatus = (data, { isOrderingUser, isMasterAccount }) => {
      const jobKey = data.printJobId || data.orderId;
      const toastKey = `${jobKey}:${data.status}`;
      if (shownPrintStatusesRef.current.has(toastKey)) return;
      shownPrintStatusesRef.current.add(toastKey);
      if (shownPrintStatusesRef.current.size > 200) {
        shownPrintStatusesRef.current = new Set([...shownPrintStatusesRef.current].slice(-100));
      }

      const bill = data.billNumber ? ` (Bill # ${data.billNumber})` : "";

      if (isFinalKitchenReceiptPrintStatus(data.status)) {
        clearPrintStatusTimer(jobKey);
      } else if (isOrderingUser) {
        // Fail instead of waiting forever when no final status arrives (the backend also fails jobs on master disconnect).
        clearPrintStatusTimer(jobKey);
        printStatusTimersRef.current.set(
          jobKey,
          setTimeout(() => {
            printStatusTimersRef.current.delete(jobKey);
            notifyPrintStatus(
              {
                ...data,
                status: KITCHEN_RECEIPT_PRINT_STATUS.FAILED,
                message: "No print confirmation from the kitchen receipt master account",
              },
              { isOrderingUser, isMasterAccount }
            );
          }, PRINT_STATUS_TIMEOUT_MS)
        );
      }

      if (data.status === KITCHEN_RECEIPT_PRINT_STATUS.PRINTING) {
        enqueueSnackbar(`Kitchen receipt is printing...${bill}`, { variant: "info" });
      } else if (data.status === KITCHEN_RECEIPT_PRINT_STATUS.PRINTED) {
        enqueueSnackbar(`Kitchen receipt printed successfully.${bill}`, { variant: "success" });
      } else if (data.status === KITCHEN_RECEIPT_PRINT_STATUS.FAILED) {
        enqueueSnackbar(`Kitchen receipt printing failed.${bill}${data.message ? `\n${data.message}` : ""}`, {
          variant: "error",
          autoHideDuration: 8000,
          style: { whiteSpace: "pre-line" },
        });
      } else if (data.status === KITCHEN_RECEIPT_PRINT_STATUS.SKIPPED && isMasterAccount && data.message) {
        enqueueSnackbar(`Kitchen receipt not printed${bill}: ${data.message}`, { variant: "info" });
      }
    };

    // Shows the status on this master account immediately, then sends it to the backend,
    // which relays it only to the master account and the ordering user.
    const emitPrintStatus = (job, status, { message = "", failedStations = [] } = {}) => {
      if (!job?.orderId) return;
      const payload = { ...job, status, message, failedStations, masterAccountId: String(userRef.current?.id) };
      notifyPrintStatus(payload, { isOrderingUser: false, isMasterAccount: true });
      debugKitchenPrint(`emit ${KITCHEN_RECEIPT_PRINT_STATUS_EVENT}`, { status, socketConnected: socket.connected, payload });
      socket.emit(KITCHEN_RECEIPT_PRINT_STATUS_EVENT, payload);
    };

    const printOrder = async (order, action) => {
      const currentUser = userRef.current;
      const { receiptPrinterIPs = [] } = currentUser?.venue ?? {};
      const orderVenueId = getEntityId(order.venueId);
      const orderLevelId = getEntityId(order.levelId);

      // Kitchen stations belong to the order's level, which may differ from the level selected here.
      const receiptResponse = await axios.get(
        `${backendUrl}/venueReceipt?venueId=${orderVenueId}&levelId=${orderLevelId}`
      );
      const receiptList = receiptResponse?.data?.results || [];

      const orderItems = order.items || [];
      const nonVoidedItems = filterOutVoidItems(orderItems);
      let printers;

      if (action === "updated") {
        const changeCheckItems = getItemsForStationChangeCheck(order);
        printers = getPrintersForReceipts(receiptList, receiptPrinterIPs, orderItems).filter((printer) =>
          hasStationDataChanged(printer.receipt.categories, changeCheckItems)
        );
      } else {
        printers = getPrintersForReceipts(receiptList, receiptPrinterIPs, nonVoidedItems);

        // Same fallback as the POS add-order flow: no station printer matched, use the default receipt printer.
        if (printers.length === 0 && nonVoidedItems.length > 0) {
          const defaultReceipt = getDefaultReceipt(receiptList);
          const defaultPrinter = getDefaultPrinter(receiptPrinterIPs, defaultReceipt);
          if (defaultPrinter?.ipAddress) {
            printers = [{
              receipt: { ...defaultReceipt, categories: [] },
              ipAddress: defaultPrinter.ipAddress,
              port: defaultPrinter.port || 9100,
            }];
          }
        }
      }

      debugKitchenPrint("printers selected", { action, printers: printers.map((p) => ({ station: p.receipt?.name, ip: p.ipAddress, port: p.port })) });
      if (printers.length === 0) {
        if (action === "updated") {
          return { status: KITCHEN_RECEIPT_PRINT_STATUS.SKIPPED, message: "No kitchen changes to print" };
        }
        if (nonVoidedItems.length === 0) {
          return { status: KITCHEN_RECEIPT_PRINT_STATUS.SKIPPED, message: "No kitchen items to print" };
        }
        return {
          status: KITCHEN_RECEIPT_PRINT_STATUS.FAILED,
          message: "No kitchen printer IP is configured for this order's items",
        };
      }

      const defaultReceipt = getDefaultReceipt(receiptList);
      const { venueAllUser = [] } = store.getState()?.venueUser ?? {};
      const { tables = [] } = store.getState()?.table ?? {};
      const results = [];

      for (const printer of printers) {
        try {
          const receiptHTML = ReactDOMServer.renderToStaticMarkup(
            <KitchenReceiptThroughIPAutoStation
              openDefault={defaultReceipt ? printer.receipt._id === defaultReceipt._id : false}
              user={currentUser}
              venueAllUser={venueAllUser}
              tables={tables}
              posCart={order}
              selectedCategories={{
                categories: printer.receipt.categories || [],
                name: printer.receipt.name || "",
              }}
              receiptList={receiptList}
              cartType={order.orderType || order.cartType || ""}
              name={order.label || ""}
              customer={order.customer || order.customerObject}
              assignee={getEntityId(order.assignee)}
              tableNumber={order.tableNumber}
              tableID={getEntityId(order.tableId)}
            />
          );

          const res = await autoPrint({
            receiptData: receiptHTML,
            ipAddress: printer.ipAddress,
            port: printer.port,
          });
          results.push({ ...res, ipAddress: printer.ipAddress });

          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (printError) {
          results.push({ success: false, message: printError.message, ipAddress: printer.ipAddress });
        }
      }

      debugKitchenPrint("print service results", results);
      showPrintResults(results, printers);

      // Only a confirmed success from the print service counts as printed.
      const failedIPs = results.filter((res) => res.success !== true).map((res) => String(res.ipAddress));
      const failedStations = printers
        .filter((printer) => failedIPs.includes(String(printer.ipAddress)))
        .map((printer) => printer.receipt?.name || printer.ipAddress);

      if (failedStations.length === 0) {
        return { status: KITCHEN_RECEIPT_PRINT_STATUS.PRINTED, message: "" };
      }
      return {
        status: KITCHEN_RECEIPT_PRINT_STATUS.FAILED,
        message: `Print failed: ${failedStations.join(", ")}`,
        failedStations,
      };
    };

    const handleMasterAccountOrder = ({ venueId, orderId, action, printJobId, billNumber, orderedByVenueUserId } = {}) => {
      const currentUser = userRef.current;
      debugKitchenPrint(`received ${KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT}`, { venueId, orderId, action, printJobId, orderedByVenueUserId });
      if (!orderId || !currentUser?.id) return;
      if (String(venueId) !== getEntityId(currentUser.venueId || currentUser.venue?.id)) {
        debugKitchenPrint("ignored: different venue", { venueId, userVenueId: currentUser.venueId });
        return;
      }

      // Identifies this print for both sides; printJobId becomes the saved order version (id:updatedAt) once fetched.
      const job = {
        venueId: String(venueId),
        orderId: String(orderId),
        action,
        printJobId: printJobId || null,
        billNumber: billNumber ?? null,
        orderedByVenueUserId: orderedByVenueUserId || null,
      };

      runKitchenReceiptMasterAccountExclusive(async () => {
        let step = "fetching the order";
        try {
          const response = await axios.get(`${backendUrl}/order/${orderId}`);
          const order = response?.data;
          if (!order) {
            emitPrintStatus(job, KITCHEN_RECEIPT_PRINT_STATUS.FAILED, { message: "Order not found" });
            return;
          }

          const versionKey = getOrderVersionKey(order);
          job.printJobId = job.printJobId || versionKey;
          job.billNumber = job.billNumber ?? order.billNumber ?? null;
          job.orderedByVenueUserId = job.orderedByVenueUserId || getEntityId(order.venueUserId) || null;

          // Another tab of this browser already handled this saved version and reported its status.
          if (isOrderVersionProcessed(versionKey)) {
            debugKitchenPrint("ignored: already handled", versionKey);
            return;
          }
          markOrderVersionProcessed(versionKey);

          step = "refreshing Open Orders";
          // Existing Open Orders refresh: server counts plus the POS list reload in CheckoutCart.
          dispatch(getOrderAndDraftCount(currentUser.venue?.id, getSelectedLevelId(currentUser)));
          window.dispatchEvent(
            new CustomEvent(KITCHEN_RECEIPT_MASTER_ACCOUNT_OPEN_ORDERS_REFRESH, { detail: { orderId, action } })
          );

          // This account saved the order itself, so its own POS flow already handled the kitchen slip.
          const isSavedByThisAccount = getEntityId(order.venueUserId) === String(currentUser.id);
          // Same rule as the POS: with KDS on, kitchen slips print only when enabled after KDS.
          const { isKitchenFeatureAvailed, isPrintKitchenReceiptEvenAfterKDS } = currentUser.venue?.configurations ?? {};
          const isKitchenSlipEnabled = !isKitchenFeatureAvailed || isPrintKitchenReceiptEvenAfterKDS;

          debugKitchenPrint("order fetched", { versionKey, isSavedByThisAccount, isKitchenSlipEnabled, orderVenueUserId: order.venueUserId });
          if (!isSavedByThisAccount) {
            if (!isKitchenSlipEnabled) {
              emitPrintStatus(job, KITCHEN_RECEIPT_PRINT_STATUS.SKIPPED, {
                message: "Kitchen slips are turned off while the Kitchen Display System is on",
              });
            } else {
              step = "printing the kitchen receipt";
              emitPrintStatus(job, KITCHEN_RECEIPT_PRINT_STATUS.PRINTING);
              const printResult = await printOrder(order, action);
              emitPrintStatus(job, printResult.status, {
                message: printResult.message,
                failedStations: printResult.failedStations,
              });
            }
          }

          step = "saving the order snapshot";
          saveOrderSnapshot(order);
        } catch (error) {
          // utils/axios rejects with the backend response body, so the reason can be a string or an object
          const reason = typeof error === "string" ? error : error?.message || "Unknown error";
          console.error(`Kitchen receipt master account order failed while ${step} (order ${orderId}, ${action}):`, error);
          enqueueSnackbar(`Unable to auto print kitchen receipt while ${step}: ${reason}`, {
            variant: "error",
            autoHideDuration: 8000,
          });
          emitPrintStatus(job, KITCHEN_RECEIPT_PRINT_STATUS.FAILED, { message: `Failed while ${step}: ${reason}` });
        }
      });
    };

    // Received by the ordering user and the master account only; the backend targets their rooms.
    const handlePrintStatus = (data = {}) => {
      const currentUser = userRef.current;
      debugKitchenPrint(`received ${KITCHEN_RECEIPT_PRINT_STATUS_EVENT}`, data);
      if (!currentUser?.id || !data.orderId) return;
      if (String(data.venueId) !== getEntityId(currentUser.venueId || currentUser.venue?.id)) return;

      const isOrderingUser = String(data.orderedByVenueUserId) === String(currentUser.id);
      const isMasterAccount = String(data.masterAccountId) === String(currentUser.id);
      if (!isOrderingUser && !isMasterAccount) return;

      notifyPrintStatus(data, { isOrderingUser, isMasterAccount });
    };

    socket.on(KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT, handleMasterAccountOrder);
    socket.on(KITCHEN_RECEIPT_PRINT_STATUS_EVENT, handlePrintStatus);
    return () => {
      socket.off(KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT, handleMasterAccountOrder);
      socket.off(KITCHEN_RECEIPT_PRINT_STATUS_EVENT, handlePrintStatus);
      printStatusTimersRef.current.forEach((timer) => clearTimeout(timer));
      printStatusTimersRef.current.clear();
    };
  }, [user?.id, dispatch, enqueueSnackbar]);

  return null;
}
